import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { boardQueryOptions, type BoardMember } from "@/lib/board.queries";
import {
  createBoardMember,
  deleteBoardMember,
  updateBoardMember,
} from "@/lib/board.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Users } from "lucide-react";
import { MediaField } from "@/components/admin/MediaField";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";
import { MEDIA_TARGETS } from "@/lib/media-buckets";

export const Route = createFileRoute("/_authenticated/admin/board")({
  component: BoardAdminPage,
});

type FormState = {
  id?: string;
  first_name: string;
  last_name: string;
  title: string;
  org: string;
  bio: string;
  photo_url: string;
  linkedin_url: string;
  display_order: number;
};

const emptyForm: FormState = {
  first_name: "",
  last_name: "",
  title: "",
  org: "",
  bio: "",
  photo_url: "",
  linkedin_url: "",
  display_order: 100,
};

function BoardAdminPage() {
  const { data: members = [] } = useQuery(boardQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createBoardMember);
  const update = useServerFn(updateBoardMember);
  const del = useServerFn(deleteBoardMember);

  const [open, setOpen] = useState(false);
  // True while the MediaField's Upload/Library picker (sibling modal) is up.
  const [pickerBusy, setPickerBusy] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const dirty = useDirty(form, open);

  const sorted = useMemo(
    () =>
      [...members].sort(
        (a, b) =>
          a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name),
      ),
    [members],
  );

  const saveMut = useMutation({
    mutationFn: async (f: FormState) => {
      const payload = {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        title: f.title,
        org: f.org,
        bio: f.bio,
        photo_url: f.photo_url,
        linkedin_url: f.linkedin_url,
        display_order: Number(f.display_order) || 0,
      };
      if (f.id) return update({ data: { ...payload, id: f.id } });
      return create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["board_members"] });
      setOpen(false);
      setForm(emptyForm);
      toast.success("Saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["board_members"] });
      toast.success("Removed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const openEdit = (m: BoardMember) => {
    setForm({
      id: m.id,
      first_name: m.first_name,
      last_name: m.last_name,
      title: m.title ?? "",
      org: m.org ?? "",
      bio: m.bio ?? "",
      photo_url: m.photo_url ?? "",
      linkedin_url: m.linkedin_url ?? "",
      display_order: m.display_order,
    });
    setOpen(true);
  };

  const openNew = () => {
    setForm({ ...emptyForm });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Board of Directors</CardTitle>
          <CardDescription>Sorted alphabetically by last name on admin and public pages.</CardDescription>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> Add member
        </Button>
        <FormDialog
          open={open}
          onOpenChange={setOpen}
          size="md"
          icon={Users}
          title={form.id ? "Edit board member" : "Add board member"}
          context={form.id ? `${form.first_name} ${form.last_name}`.trim() : "New board member"}
          dirty={dirty}
          busy={pickerBusy || saveMut.isPending}
          asForm
          onSubmit={() => saveMut.mutate(form)}
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <SaveButton dirty={dirty} pending={saveMut.isPending} />
            </>
          }
        >
          <div className="space-y-4">
            <FormSection title="Identity">
              <Field label="First name" required>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </Field>
              <Field label="Last name" required>
                <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </Field>
              <Field label="Title">
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Board Advisor" />
              </Field>
              <Field label="Organization">
                <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
              </Field>
            </FormSection>
            <FormSection title="Profile">
              <div className="sm:col-span-2">
                <MediaField
                  label="Photo"
                  value={form.photo_url}
                  target={MEDIA_TARGETS.boardPhoto}
                  onChange={(url) => setForm((f) => ({ ...f, photo_url: url }))}
                  onPickerOpenChange={setPickerBusy}
                />
              </div>
              <Field label="LinkedIn URL" full>
                <Input type="url" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} />
              </Field>
              <Field label="Bio" full>
                <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              </Field>
            </FormSection>
          </div>
        </FormDialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Last name</TableHead>
              <TableHead>First name</TableHead>
              <TableHead className="hidden md:table-cell">Title</TableHead>
              <TableHead className="hidden lg:table-cell">Org</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-medium">{m.last_name}</TableCell>
                <TableCell>{m.first_name}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{m.title}</TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground">{m.org}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(m)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm(`Remove ${m.first_name} ${m.last_name}?`)) delMut.mutate(m.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No board members yet — add the first.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

